import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileText, User, Calendar, Clock,
  Download, Printer, Send, CheckCircle, XCircle,
  Phone, Mail, MapPin, Package, IndianRupee, Edit2, ArrowRight,
  MessageCircle, Loader2,
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { proformaInvoicesAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const ProformaInvoiceDetailPage = () => {
  const { businessId, id: proformaId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const bid = businessId || activeBusiness?.id;

  const [proforma, setProforma] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    if (bid && proformaId) fetchProforma();
  }, [bid, proformaId]);

  const fetchProforma = async () => {
    setLoading(true);
    try {
      const response = await proformaInvoicesAPI.get(proformaId);
      setProforma(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load proforma'));
      navigate(`/business/${bid}/proforma-invoices`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      DRAFT: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft' },
      SENT: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Sent' },
      ACCEPTED: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Accepted' },
      REJECTED: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
      EXPIRED: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Expired' },
      CONVERTED: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Converted' },
      CANCELLED: { bg: 'bg-slate-100', text: 'text-slate-500', label: 'Cancelled' }
    };
    return configs[status] || configs.DRAFT;
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  // State-change actions route through ConfirmModal -- matches the
  // quotations + sales_orders detail page patterns. Silent api.patch
  // felt OK but gave no protection against accidental clicks on a
  // live proforma's Send/Accept buttons.
  const handleMarkSent = () => {
    setConfirmModal({
      title: 'Mark as Sent',
      description: 'Mark this proforma as sent to the customer?',
      confirmLabel: 'Mark as Sent',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      icon: Send,
      iconClass: 'text-blue-600',
      iconBgClass: 'bg-blue-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await proformaInvoicesAPI.updateStatus(proformaId, 'SENT');
          toast.success('Marked as Sent');
          fetchProforma();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to update status'));
        }
      },
    });
  };

  const handleMarkAccepted = () => {
    setConfirmModal({
      title: 'Mark as Accepted',
      description: 'Record that the customer accepted this proforma?',
      confirmLabel: 'Mark Accepted',
      confirmClass: 'bg-emerald-600 hover:bg-emerald-700',
      icon: CheckCircle,
      iconClass: 'text-emerald-600',
      iconBgClass: 'bg-emerald-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await proformaInvoicesAPI.updateStatus(proformaId, 'ACCEPTED');
          toast.success('Marked as Accepted');
          fetchProforma();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to update status'));
        }
      },
    });
  };

  const handleReject = () => {
    setRejectReason('');
    setConfirmModal({
      title: 'Reject Proforma',
      description: 'Record the customer\u2019s reason for rejecting so your team has context later.',
      confirmLabel: 'Reject',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: XCircle,
      iconClass: 'text-red-600',
      iconBgClass: 'bg-red-100',
      kind: 'reject',
      onConfirm: async (reason) => {
        setConfirmModal(null);
        try {
          await proformaInvoicesAPI.updateStatus(proformaId, 'REJECTED', reason || '');
          toast.success('Marked as Rejected');
          fetchProforma();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to reject proforma'));
        }
      },
    });
  };

  const handleConvertToInvoice = () => {
    setConfirmModal({
      title: 'Convert to Invoice',
      description: 'A new Sales Invoice (DRAFT) will be created from this proforma.',
      confirmLabel: 'Convert',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      icon: ArrowRight,
      iconClass: 'text-blue-600',
      iconBgClass: 'bg-blue-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const response = await proformaInvoicesAPI.convertToInvoice(proformaId);
          const data = response.data || {};
          const invoice = data.invoice || {};
          toast.success(`Converted to invoice ${invoice.invoice_number || ''}`);
          if (invoice.id) {
            navigate(`/business/${bid}/sales/invoices/${invoice.id}`);
          } else {
            fetchProforma();
          }
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to convert to invoice'));
        }
      },
    });
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await proformaInvoicesAPI.downloadPdf(proformaId);
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Proforma_${proforma?.proforma_number || proformaId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to download PDF'));
    }
  };

  // Stub -- backend writes to proforma_invoices.activity_log, returns
  // {success, message, note: "Email integration pending - action logged"}.
  // Surface both so the user knows the action was recorded + delivery
  // is pending. Mirrors the quotations + sales_orders pattern.
  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      const response = await proformaInvoicesAPI.sendEmail(proformaId);
      const data = response.data || {};
      toast.success(data.message || 'Email queued');
      if (data.note) toast(data.note, { icon: '\u2139\ufe0f', duration: 4000 });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to queue email'));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendWhatsApp = async () => {
    try {
      const response = await proformaInvoicesAPI.sendWhatsApp(proformaId);
      const data = response.data || {};
      toast.success(data.message || 'WhatsApp message queued');
      if (data.note) toast(data.note, { icon: '\u2139\ufe0f', duration: 4000 });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to queue WhatsApp'));
    }
  };

  const isRejectFlow = confirmModal?.kind === 'reject';

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
          <p className="text-[13px] text-slate-500">Loading proforma invoice...</p>
        </div>
      </div>
    );
  }

  if (!proforma) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">Proforma invoice not found</p>
          <button onClick={() => navigate(`/business/${bid}/proforma-invoices`)} className="mt-4 text-blue-600 hover:underline text-[14px]">
            Back to Proforma Invoices
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(proforma.status);
  const items = proforma.items || [];

  return (
    <>
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageSlideIn">
      {/* ══════════════════════════════════════════════════════════════════════
          PREMIUM HEADER - Commercial Grade Design
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* LEFT: Back Button + Title + Status */}
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(`/business/${bid}/proforma-invoices`)}
                className="group flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-[18px] font-semibold text-slate-800">{proforma.proforma_number}</h1>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${statusConfig.bg} ${statusConfig.text}`}>
                    {statusConfig.label}
                  </span>
                </div>
                <p className="text-[12px] text-slate-500 mt-0.5">Created {formatDate(proforma.created_at)}</p>
              </div>
            </div>
            
            {/* RIGHT: Action Buttons */}
            <div className="flex items-center gap-2">
              {proforma.status === 'DRAFT' && (
                <>
                  <button
                    onClick={() => navigate(`/business/${bid}/proforma-invoices/${proformaId}/edit`)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-all text-[13px] font-medium"
                  >
                    <Edit2 className="w-4 h-4" /> Edit
                  </button>
                  <button
                    onClick={handleMarkSent}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-[13px] font-medium"
                  >
                    <Send className="w-4 h-4" /> Send
                  </button>
                </>
              )}
              {proforma.status === 'SENT' && (
                <>
                  <button
                    onClick={handleMarkAccepted}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all text-[13px] font-medium"
                  >
                    <CheckCircle className="w-4 h-4" /> Mark Accepted
                  </button>
                  <button
                    onClick={handleReject}
                    className="inline-flex items-center gap-2 px-4 py-2 text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all text-[13px] font-medium"
                  >
                    <XCircle className="w-4 h-4" /> Reject
                  </button>
                </>
              )}
              {['DRAFT', 'SENT', 'ACCEPTED'].includes(proforma.status) && (
                <button
                  onClick={handleConvertToInvoice}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all text-[13px] font-medium"
                >
                  <ArrowRight className="w-4 h-4" /> Convert to Invoice
                </button>
              )}
              <div className="w-px h-8 bg-slate-200 mx-1" />
              <button
                onClick={handleDownloadPdf}
                className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-all"
                title="Download PDF"
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                title="Send via Email"
              >
                {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              </button>
              <button
                onClick={handleSendWhatsApp}
                className="inline-flex items-center justify-center w-10 h-10 text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-all"
                title="Send via WhatsApp"
              >
                <MessageCircle className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          MAIN CONTENT - Premium 3-Column Layout
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT: Customer Info Card */}
            <div className="col-span-12 lg:col-span-3">
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-[14px] font-semibold text-slate-800">Customer</h3>
                    <p className="text-[11px] text-slate-500">Billing details</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-[15px] font-semibold text-slate-800">
                      {proforma.party_name || proforma.customer_name || '—'}
                    </p>
                  </div>
                  {(proforma.party_phone || proforma.customer_phone) && (
                    <div className="flex items-center gap-2 text-[13px] text-slate-600">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span>{proforma.party_phone || proforma.customer_phone}</span>
                    </div>
                  )}
                  {(proforma.party_email || proforma.customer_email) && (
                    <div className="flex items-center gap-2 text-[13px] text-slate-600">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span>{proforma.party_email || proforma.customer_email}</span>
                    </div>
                  )}
                  {(proforma.billing_address || proforma.customer_address) && (
                    <div className="flex items-start gap-2 text-[13px] text-slate-600">
                      <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                      <span>{proforma.billing_address || proforma.customer_address}</span>
                    </div>
                  )}
                  {proforma.party_gstin && (
                    <div className="flex items-start gap-2 text-[13px] text-slate-600">
                      <FileText className="w-4 h-4 text-slate-400 mt-0.5" />
                      <span>GSTIN: {proforma.party_gstin}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Proforma Info Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200 mt-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-sm">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-[14px] font-semibold text-slate-800">Proforma Info</h3>
                    <p className="text-[11px] text-slate-500">Dates & validity</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="text-slate-500">Proforma Date</span>
                    <span className="font-medium text-slate-800">{formatDate(proforma.proforma_date)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="text-slate-500">Expiry Date</span>
                    <span className="font-medium text-slate-800">{formatDate(proforma.expiry_date)}</span>
                  </div>
                  {proforma.reference_number && (
                    <div className="flex items-center justify-between text-[13px]">
                      <span className="text-slate-500">Reference</span>
                      <span className="font-medium text-slate-800">{proforma.reference_number}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* CENTER: Items Table */}
            <div className="col-span-12 lg:col-span-6">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200/80 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-sm">
                      <Package className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-[14px] font-semibold text-slate-800">Proforma Items</h3>
                      <p className="text-[11px] text-slate-500">{items.length} item{items.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Item</th>
                        <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Qty</th>
                        <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Rate</th>
                        <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {items.map((item, index) => (
                        <tr key={index} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-4 py-3">
                            <p className="text-[13px] font-medium text-slate-800">{item.item_name || item.name}</p>
                            {item.description && <p className="text-[11px] text-slate-500 mt-0.5">{item.description}</p>}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-[13px] text-slate-700">{item.quantity} {item.unit || ''}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-[13px] text-slate-700">{formatCurrency(item.rate)}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-[13px] font-medium text-slate-800">{formatCurrency(item.total_amount || item.amount)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* RIGHT: Summary Card */}
            <div className="col-span-12 lg:col-span-3">
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-sm">
                    <IndianRupee className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-[14px] font-semibold text-slate-800">Summary</h3>
                    <p className="text-[11px] text-slate-500">Proforma totals</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="text-slate-500">Subtotal</span>
                    <span className="font-medium text-slate-800">{formatCurrency(proforma.subtotal)}</span>
                  </div>
                  {proforma.discount_amount > 0 && (
                    <div className="flex items-center justify-between text-[13px]">
                      <span className="text-slate-500">Discount</span>
                      <span className="font-medium text-emerald-600">-{formatCurrency(proforma.discount_amount)}</span>
                    </div>
                  )}
                  {proforma.tax_amount > 0 && (
                    <div className="flex items-center justify-between text-[13px]">
                      <span className="text-slate-500">Tax</span>
                      <span className="font-medium text-slate-800">{formatCurrency(proforma.tax_amount)}</span>
                    </div>
                  )}
                  <div className="h-px bg-slate-200 my-2"></div>
                  <div className="flex items-center justify-between">
                    <span className="text-[14px] font-semibold text-slate-800">Grand Total</span>
                    <span className="text-[18px] font-bold text-emerald-600">{formatCurrency(proforma.grand_total)}</span>
                  </div>
                </div>
              </div>

              {/* Notes Card */}
              {proforma.notes && (
                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200 mt-4">
                  <h3 className="text-[14px] font-semibold text-slate-800 mb-3">Notes & Terms</h3>
                  <p className="text-[13px] text-slate-600 whitespace-pre-wrap">{proforma.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

    <ConfirmModal
      isOpen={!!confirmModal}
      title={confirmModal?.title}
      description={confirmModal?.description}
      confirmLabel={confirmModal?.confirmLabel}
      confirmClass={confirmModal?.confirmClass}
      icon={confirmModal?.icon}
      iconClass={confirmModal?.iconClass}
      iconBgClass={confirmModal?.iconBgClass}
      confirmDisabled={isRejectFlow && !rejectReason.trim()}
      onConfirm={() => confirmModal?.onConfirm?.(rejectReason)}
      onClose={() => { setConfirmModal(null); setRejectReason(''); }}
    >
      {isRejectFlow && (
        <textarea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="e.g. Price too high, went with another vendor, timeline too tight..."
          rows={3}
          autoFocus
          className="w-full px-3 py-2 text-[13px] border border-slate-200 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 focus:outline-none resize-none"
        />
      )}
    </ConfirmModal>
    </>
  );
};

export default ProformaInvoiceDetailPage;
